import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Clock, 
  Users, 
  Video, 
  VolumeX, 
  Volume2, 
  CheckCircle,
  AlertCircle,
  UserCheck,
  Wifi,
  WifiOff
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { AvailabilityService } from '@/lib/services/availabilityService';
import { PresenceService } from '@/lib/services/presenceService';
import {
  getHandledActiveCalls,
  markActiveCallHandled,
  shouldAcceptActiveCall
} from '@/lib/clinic/activeCallAcceptance';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { format, formatDistanceToNow } from 'date-fns';

interface WaitingRoomProps {
  coachId: string;
  coachName: string;
  coachAvatar?: string;
  customerName: string;
  customerPhone?: string;
  onCallStarted: (roomId: string) => void;
  onLeaveQueue: () => void;
  publicCustomerInfo?: {
    customerId: string;
    customerName: string;
  };
}

export function WaitingRoom({ 
  coachId, 
  coachName, 
  coachAvatar, 
  customerName: propCustomerName,
  customerPhone,
  onCallStarted, 
  onLeaveQueue,
  publicCustomerInfo
}: WaitingRoomProps) {
  const { language } = useLanguage();
  const { currentUser, userData } = useAuth();
  
  // State
  const [isConnected, setIsConnected] = useState(true);
  const [isInRoom, setIsInRoom] = useState(false);
  const [customersCount, setCustomersCount] = useState(0);
  const [clinicStatus, setClinicStatus] = useState<'open' | 'closed'>('open');
  const [isLoading, setIsLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [joinTime] = useState<Date>(new Date());
  const [cameraPermission, setCameraPermission] = useState<'granted' | 'denied' | 'prompt'>('prompt');
  const [micPermission, setMicPermission] = useState<'granted' | 'denied' | 'prompt'>('prompt');
  
  // Refs
  const audioRef = useRef<HTMLAudioElement>(null);
  const hasJoinedCall = useRef(false);
  const myJoinTimeMs = useRef<number | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  // Get stable customerId and customerName
  const customerId = publicCustomerInfo?.customerId || currentUser?.uid || '';
  const customerName = publicCustomerInfo?.customerName || propCustomerName || 
    (userData ? `${userData.firstNameEn || userData.firstNameAr} ${userData.lastNameEn || userData.lastNameAr}` : '');

  // Check media permissions on mount
  useEffect(() => {
    checkMediaPermissions();
  }, []);

  // Join waiting room with heartbeat
  useEffect(() => {
    if (!customerId || !coachId || !customerName) return;

    const joinRoom = async () => {
      setIsLoading(true);
      
      try {
        // Join via Firestore - this starts the heartbeat
        const cleanup = await PresenceService.joinWaitingRoom(
          coachId,
          customerId,
          customerName,
          customerPhone,
          (connected) => {
            setIsConnected(connected);
            if (!connected) {
              toast.error(language === 'ar' ? 'انقطع الاتصال' : 'Connection lost');
            }
          }
        );
        
        cleanupRef.current = cleanup;
        setIsLoading(false);
      } catch (error) {
        console.error('Error joining waiting room:', error);
        toast.error(language === 'ar' ? 'فشل الانضمام لغرفة الانتظار' : 'Failed to join waiting room');
        setIsLoading(false);
      }
    };

    joinRoom();

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, [coachId, customerId, customerName, customerPhone, language]);

  // Listen to presence status
  useEffect(() => {
    if (!customerId || !coachId) return;

    const unsubscribe = PresenceService.listenToCustomerPresence(
      coachId,
      customerId,
      (inRoom, count, ownJoinTimeMs) => {
        setIsInRoom(inRoom);
        setCustomersCount(count);
        myJoinTimeMs.current = ownJoinTimeMs;
      }
    );

    return unsubscribe;
  }, [coachId, customerId]);

  // Listen to clinic status
  useEffect(() => {
    if (!coachId) return;

    const unsubscribe = PresenceService.listenToClinicStatus(coachId, (status) => {
      setClinicStatus(status);
    });

    return unsubscribe;
  }, [coachId]);

  // Listen for active call (when doctor calls this customer)
  useEffect(() => {
    if (!customerId || !coachId) return;

    const unsubscribe = AvailabilityService.listenToWaitingRoom(coachId, (waitingRoom) => {
      const activeCall = waitingRoom?.activeCall;
      if (!activeCall || hasJoinedCall.current) return;

      // With callId: accept by identity (callId/joinTime); without: the
      // legacy "for me and < 60 s old" rule. Never re-enter a handled call (#47).
      if (shouldAcceptActiveCall(activeCall, {
        customerId,
        nowMs: Date.now(),
        handled: getHandledActiveCalls(),
        myJoinTimeMs: myJoinTimeMs.current
      })) {
        hasJoinedCall.current = true;
        markActiveCallHandled(activeCall);
        handleCallReady(activeCall.roomId);
      }
    });

    return unsubscribe;
  }, [coachId, customerId]);

  const checkMediaPermissions = async () => {
    try {
      const cameraPermission = await navigator.permissions.query({ name: 'camera' as PermissionName });
      setCameraPermission(cameraPermission.state);
      
      const micPermission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      setMicPermission(micPermission.state);
    } catch (error) {
      console.log('Permission API not supported');
    }
  };

  const requestMediaPermissions = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setCameraPermission('granted');
      setMicPermission('granted');
      toast.success(language === 'ar' ? 'تم السماح بالكاميرا والميكروفون' : 'Camera and microphone access granted');
    } catch (error) {
      setCameraPermission('denied');
      setMicPermission('denied');
      toast.error(language === 'ar' ? 'تم رفض الوصول للكاميرا والميكروفون' : 'Camera and microphone access denied');
    }
  };

  const handleCallReady = (roomId: string) => {
    if (!isMuted && audioRef.current) {
      audioRef.current.play().catch(() => {});
    }
    
    toast.success(
      language === 'ar' 
        ? "حان دورك! جاري بدء المكالمة..." 
        : "It's your turn! Starting video call...", 
      { duration: 3000 }
    );
    
    setTimeout(() => {
      onCallStarted(roomId);
    }, 1000);
  };

  const getStatusIcon = (status: 'open' | 'closed') => {
    return status === 'open' 
      ? <CheckCircle className="h-4 w-4 text-green-500" />
      : <AlertCircle className="h-4 w-4 text-red-500" />;
  };

  const getStatusText = (status: 'open' | 'closed') => {
    return status === 'open'
      ? (language === 'ar' ? 'مفتوحة' : 'Open')
      : (language === 'ar' ? 'مغلقة' : 'Closed');
  };

  const getStatusColor = (status: 'open' | 'closed') => {
    return status === 'open'
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-red-100 text-red-800 border-red-200';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/20">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="flex flex-col items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
            <p className="text-muted-foreground">
              {language === 'ar' ? 'جاري الانضمام لغرفة الانتظار...' : 'Joining waiting room...'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/20 p-4">
      <div className="max-w-2xl mx-auto space-y-6">
        
        {/* Connection Status */}
        {!isConnected && (
          <Alert variant="destructive">
            <WifiOff className="h-4 w-4" />
            <AlertDescription>
              {language === 'ar' ? 'انقطع الاتصال - جاري إعادة الاتصال...' : 'Disconnected - Reconnecting...'}
            </AlertDescription>
          </Alert>
        )}
        
        {/* Header */}
        <Card>
          <CardHeader className="text-center pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {isConnected ? (
                  <Wifi className="h-4 w-4 text-green-500" />
                ) : (
                  <WifiOff className="h-4 w-4 text-red-500" />
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMuted(!isMuted)}
                className="text-muted-foreground hover:text-foreground"
              >
                {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </Button>
            </div>
            
            <div className="w-20 h-20 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4">
              {coachAvatar ? (
                <img 
                  src={coachAvatar} 
                  alt={coachName}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <UserCheck className="h-10 w-10 text-primary" />
              )}
            </div>
            
            <p className="text-sm text-muted-foreground mb-1">
              {language === 'ar' ? 'عيادة الطبيب' : "Doctor's Clinic"}
            </p>
            <CardTitle className="text-2xl">
              {language === 'ar' ? `انتظار ${coachName}` : `Waiting for ${coachName}`}
            </CardTitle>
            
            <div className="flex items-center justify-center gap-2 mt-2">
              {getStatusIcon(clinicStatus)}
              <Badge className={getStatusColor(clinicStatus)}>
                {getStatusText(clinicStatus)}
              </Badge>
            </div>
          </CardHeader>
        </Card>

        {/* Waiting Room Status */}
        <Card>
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center gap-3">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                  <Users className="h-8 w-8 text-primary" />
                </div>
              </div>
              
              <div>
                <p className="text-3xl font-bold text-primary">
                  {customersCount}
                </p>
                <p className="text-muted-foreground">
                  {language === 'ar' 
                    ? 'مستفيد في غرفة الإنتظار'
                    : 'customers waiting'
                  }
                </p>
              </div>

              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>
                  {isInRoom 
                    ? (language === 'ar' ? 'أنت في غرفة الإنتظار' : 'You are in the waiting room')
                    : (language === 'ar' ? 'جاري الانضمام...' : 'Joining...')
                  }
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Media Permissions */}
        {(cameraPermission !== 'granted' || micPermission !== 'granted') && (
          <Card>
            <CardContent className="pt-6">
              <Alert>
                <Video className="h-4 w-4" />
                <AlertDescription>
                  {language === 'ar' 
                    ? 'يرجى السماح بالوصول للكاميرا والميكروفون لبدء المكالمة'
                    : 'Please allow camera and microphone access to start the video call'
                  }
                </AlertDescription>
              </Alert>
              
              <Button 
                onClick={requestMediaPermissions}
                className="w-full mt-4"
                variant="outline"
              >
                <Video className="h-4 w-4 mr-2" />
                {language === 'ar' ? 'اختبار الكاميرا والميكروفون' : 'Test Camera & Microphone'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Doctor Ready Alert */}
        <AnimatePresence>
          {clinicStatus === 'open' && isInRoom && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="border-green-200 bg-green-50">
                <CardContent className="pt-6">
                  <div className="text-center space-y-2">
                    <CheckCircle className="h-8 w-8 text-green-600 mx-auto" />
                    <p className="text-green-800 font-medium">
                      {language === 'ar' 
                        ? 'الطبيب سيتصل بك قريباً'
                        : 'The doctor will call you soon'
                      }
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Waiting Info */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3 text-sm text-muted-foreground">
              <div className="flex items-center justify-between">
                <span>{language === 'ar' ? 'انضممت في:' : 'Joined at:'}</span>
                <span>{format(joinTime, 'HH:mm')}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span>{language === 'ar' ? 'وقت الانتظار:' : 'Waiting time:'}</span>
                <span>{formatDistanceToNow(joinTime)}</span>
              </div>
              
              <div className="pt-2 border-t">
                <p className="text-xs text-center">
                  {language === 'ar' 
                    ? 'يرجى البقاء في هذه الصفحة حتى يتصل بك الطبيب'
                    : 'Please stay on this page until the doctor calls you'
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Notification Sound */}
      <audio 
        ref={audioRef} 
        preload="auto"
        className="hidden"
      >
        <source src="/notification-sound.mp3" type="audio/mpeg" />
      </audio>
    </div>
  );
}
